import { gql } from "@apollo/client";
import {
  AgentChatThreadId,
  CollectionId,
  HexId,
  SpecialVersionType,
  StarredType,
  hexIdToHumanHexId,
} from "@hex/common";
import React, { useCallback } from "react";
import { useTheme } from "styled-components";

import { HexMenu, HexMenuItem } from "../../../hex-components";
import { useOnClickProps } from "../../../hooks/useOnClickProps.js";
import { useTerminology } from "../../../hooks/useTerminology.js";
import { ORG_ID } from "../../../orgs";
import { Routes } from "../../../route/routes";
import { useToaster } from "../../common/Toasts";
import { StarredCollectionForSidebarFragment } from "../../global-sidebar/FavoriteCollections.generated";
import {
  ArrowTopRightIcon,
  EditIcon,
  LinkIcon,
  StarActiveIcon,
} from "../../icons/CustomIcons";

import { FavoriteProjectRowOptionsMenuHexFragment } from "./HomeSideBarProjectMoreOptions.generated.js";

gql`
  fragment FavoriteProjectRowOptionsMenuHexFragment on Hex {
    id
    canEdit
    canViewLogic
    hexType
  }
`;

interface HomeSidebarProjectMoreOptionsProps {
  href: string;
  onRemoveFavorite: () => void;
  id:
    | FavoriteProjectRowOptionsMenuHexFragment["id"]
    | StarredCollectionForSidebarFragment["id"]
    | AgentChatThreadId;
  title: string;
  canEdit: FavoriteProjectRowOptionsMenuHexFragment["canEdit"];
  type: StarredType;
  canViewLogic: FavoriteProjectRowOptionsMenuHexFragment["canViewLogic"];
  isPublished: boolean;
  isStarred: boolean;
  collectionName?: string;
}

export const HomeSidebarProjectMoreOptions: React.FunctionComponent<HomeSidebarProjectMoreOptionsProps> =
  React.memo(function HomeSidebarProjectMoreOptions({
    canEdit,
    canViewLogic,
    collectionName,
    href,
    id,
    isPublished,
    isStarred,
    onRemoveFavorite,
    title,
    type,
  }) {
    const { userActionColor } = useTheme();
    const toaster = useToaster();
    const { logicText, referringToPublishedAppText: appText } =
      useTerminology();

    const openInNewTabCallback = useCallback(() => {
      window.open(href, "_blank")?.focus();
    }, [href]);

    const showCopyToClipboardToast = useCallback(
      (text: string) => {
        toaster.show({
          message: (
            <>
              Link to <strong>{text}</strong> copied to clipboard.
            </>
          ),
          timeout: 2500,
        });
      },
      [toaster],
    );
    const copyLinkToClipboard = useCallback(
      async ({ link, text }: { link: string; text?: string }) => {
        await navigator.clipboard.writeText(link);
        if (text) showCopyToClipboardToast(text);
      },
      [showCopyToClipboardToast],
    );

    const copyLinkToPublish = useCallback(async () => {
      if (!HexId.guard(id)) {
        throw new Error(`Expected ID to have type HexId: ${id}`);
      }
      const hexLocator = hexIdToHumanHexId(id, title);
      await copyLinkToClipboard({
        link: Routes.APP_LATEST.href(ORG_ID, true, {
          hexLocator,
        }),
        text: appText,
      });
    }, [copyLinkToClipboard, id, title, appText]);

    const copyLinkToLogic = useCallback(async () => {
      if (type === StarredType.COLLECTION) return;
      if (!HexId.guard(id)) {
        throw new Error(`Expected ID to have type HexId: ${id}`);
      }
      const hexLocator = hexIdToHumanHexId(id, title);
      await copyLinkToClipboard({
        link: (type === StarredType.COMPONENT
          ? Routes.COMPONENT
          : Routes.LOGIC
        ).href(ORG_ID, true, {
          hexLocator,
          version: SpecialVersionType.DRAFT,
        }),
        text: `${logicText} view`,
      });
    }, [copyLinkToClipboard, id, title, type, logicText]);

    const copyCollectionLink = useCallback(async () => {
      if (!collectionName) return;
      if (!CollectionId.guard(id)) {
        throw new Error(`Expected ID to have type CollectionId: ${id}`);
      }

      await copyLinkToClipboard({
        link: Routes.COLLECTION.href(ORG_ID, true, {
          collectionId: id,
          collectionName,
        }),
      });
    }, [copyLinkToClipboard, id, collectionName]);

    const copyPrimaryLink = useCallback(async () => {
      if (type === StarredType.COLLECTION) {
        await copyCollectionLink();
      } else if (canViewLogic || type === StarredType.COMPONENT) {
        await copyLinkToLogic();
      } else {
        await copyLinkToPublish();
      }
    }, [
      canViewLogic,
      copyLinkToLogic,
      copyLinkToPublish,
      type,
      copyCollectionLink,
    ]);

    const stopPropagation: React.MouseEventHandler = useCallback(
      (e) => e.stopPropagation(),
      [],
    );

    const logicViewUrl = () => {
      if (!HexId.guard(id)) {
        throw new Error(`Expected ID to have type HexId: ${id}`);
      }
      return Routes.LOGIC.getUrl({
        hexLocator: hexIdToHumanHexId(id, title),
        version: SpecialVersionType.DRAFT,
      });
    };

    const onClickLogicLink = useOnClickProps({
      to: logicViewUrl,
    });

    return (
      <>
        <HexMenu onClick={stopPropagation}>
          {isStarred && (
            <HexMenuItem
              icon={<StarActiveIcon color={userActionColor} />}
              text="Remove favorite"
              onClick={onRemoveFavorite}
            />
          )}
          {isPublished && canViewLogic && type !== StarredType.COLLECTION && (
            <HexMenuItem
              icon={<EditIcon />}
              text={canEdit ? "Edit draft" : "View notebook"}
              {...onClickLogicLink}
            />
          )}
          <HexMenuItem
            icon={<ArrowTopRightIcon />}
            text="Open in new tab"
            onClick={openInNewTabCallback}
          />
          {type === StarredType.COLLECTION ? (
            <HexMenuItem
              icon={<LinkIcon />}
              text="Copy link"
              onClick={copyCollectionLink}
            />
          ) : isPublished && type === StarredType.PROJECT && canViewLogic ? (
            <HexMenuItem icon={<LinkIcon />} text="Copy link">
              <HexMenuItem text={`to ${logicText}`} onClick={copyLinkToLogic} />
              <HexMenuItem text={`to ${appText}`} onClick={copyLinkToPublish} />
            </HexMenuItem>
          ) : (
            <HexMenuItem
              icon={<LinkIcon />}
              text="Copy link"
              onClick={copyPrimaryLink}
            />
          )}
        </HexMenu>
      </>
    );
  });
